import cn from 'classnames'

import { Button } from 'core-components/library/button'
import { LoaderOverlay } from 'core-components/library/loader'
import { ModalDialog } from 'core-components/library/modalDialog'

import { SMB_GUIDED_WIZARD_TEXT as TEXT } from '../../configs/guidedWizardTranslations'
import {
  TPlanningSmbGuidedWizard,
  usePlanningSmbGuidedWizard,
} from '../../hooks/usePlanningSmbGuidedWizard'
import { IPlanningSmbGuidedWizardProps } from '../../model/guidedWizardTypes'

import { PlanningSmbGuidedDetails } from './components/PlanningSmbGuidedDetails'
import { PlanningSmbGuidedOverview } from './components/PlanningSmbGuidedOverview'
import { PlanningSmbGuidedStart } from './components/PlanningSmbGuidedStart'
import {
  PlanningSmbGuidedText as Text,
  TGuidedTranslationParams,
  usePlanningSmbGuidedTranslation,
} from './components/PlanningSmbGuidedText'

import styles from './PlanningSmbGuidedWizard.module.scss'

export interface IPlanningSmbGuidedWizardContentProps {
  props: IPlanningSmbGuidedWizardProps
  editor: TPlanningSmbGuidedWizard
  params: TGuidedTranslationParams
}

interface IGuidedOverwriteContentProps {
  params: TGuidedTranslationParams
  onConfirm: () => void
  onReject: () => void
}

const GuidedOverwriteContent = ({ params, onConfirm, onReject }: IGuidedOverwriteContentProps) => (
  <div className={styles.confirmationContent}>
    <h2>
      <Text text={TEXT.overwriteTitle} />
    </h2>
    <p>
      <Text text={TEXT.overwriteBody} params={params} />
    </p>
    <div className={styles.footerActions}>
      <Button variant="text" color="grey" className={styles.cancelButton} onClick={onReject}>
        <Text text={TEXT.overwriteKeep} />
      </Button>
      <Button color="primary" className={styles.saveButton} onClick={onConfirm}>
        <Text text={TEXT.overwriteApply} />
      </Button>
    </div>
  </div>
)

export const PlanningSmbGuidedWizardContent = ({
  props,
  editor,
  params,
}: IPlanningSmbGuidedWizardContentProps) => (
  <div className={styles.content} aria-busy={editor.isApplying}>
    <div className={styles.scrollBody}>
      {editor.screen === 'start' && <PlanningSmbGuidedStart params={params} />}
      {editor.screen === 'overview' && (
        <PlanningSmbGuidedOverview props={props} editor={editor} params={params} />
      )}
      {editor.screen === 'details' && (
        <PlanningSmbGuidedDetails props={props} editor={editor} params={params} />
      )}
    </div>
    <div className={styles.footer}>
      {editor.screen !== 'start' && (
        <button
          type="button"
          className={styles.modeLink}
          disabled={editor.isApplying}
          onClick={() => editor.changeScreen(editor.screen === 'overview' ? 'details' : 'overview')}
        >
          <Text text={editor.screen === 'overview' ? TEXT.more : TEXT.back} />
        </button>
      )}
      <div className={styles.footerActions}>
        <Button
          variant="text"
          color="grey"
          className={styles.cancelButton}
          disabled={editor.isApplying}
          onClick={editor.close}
        >
          <Text text={editor.screen === 'start' ? TEXT.notNow : TEXT.cancel} />
        </Button>
        <Button
          color="primary"
          variant="contained"
          className={styles.saveButton}
          disabled={editor.isApplying}
          loading={editor.isSaving}
          onClick={editor.screen === 'start' ? () => editor.changeScreen('overview') : editor.save}
        >
          <Text
            text={
              editor.screen === 'start' ? TEXT.start : editor.isSaving ? TEXT.saving : TEXT.save
            }
          />
        </Button>
      </div>
    </div>
    <LoaderOverlay className={styles.savingOverlay} show={editor.isSaving} />
    {editor.isConfirming && (
      <ModalDialog<IGuidedOverwriteContentProps>
        show
        size="sm"
        closeOnOutsideClick={false}
        classes={{
          window: cn(styles.window, styles.confirmationWindow),
          header: styles.confirmationHeader,
          body: styles.modalBody,
        }}
        content={GuidedOverwriteContent}
        contentProps={{
          params,
          onConfirm: () => editor.resolveConfirmation(true),
          onReject: () => editor.resolveConfirmation(false),
        }}
        onClose={() => undefined}
      />
    )}
  </div>
)

export const PlanningSmbGuidedWizardModal = (props: IPlanningSmbGuidedWizardProps) => {
  const editor = usePlanningSmbGuidedWizard(props)
  const translate = usePlanningSmbGuidedTranslation()
  const lastPlanYear = editor.years[editor.years.length - 1]
  const formatYear = (year: number) => translate(TEXT.fiscalYear, { year: String(year).slice(-2) })
  const params: TGuidedTranslationParams = {
    baselineYear: String(props.fYear - 1).slice(-2),
    fYear: String(props.fYear).slice(-2),
    fYearNext: String(props.fYear + 1).slice(-2),
    fYearNextTwo: String(props.fYear + 2).slice(-2),
    lastPlanYear: String(lastPlanYear).slice(-2),
    planYearCount: editor.years.length,
    planPeriod:
      props.fYear === lastPlanYear
        ? formatYear(props.fYear)
        : `${formatYear(props.fYear)}\u2013${formatYear(lastPlanYear)}`,
    programType: props.programType,
  }
  return (
    <ModalDialog<IPlanningSmbGuidedWizardContentProps>
      show={props.show}
      size="auto"
      closeOnOutsideClick={false}
      classes={{
        window: cn(styles.window, { [styles.wideWindow]: editor.screen === 'details' }),
        header: styles.modalHeader,
        headerBodyContent: styles.headerBody,
        body: styles.modalBody,
        closeButton: styles.closeButton,
      }}
      header={() => (
        <div className={styles.header}>
          <h1 className={styles.title}>
            <Text text={TEXT.title} />
          </h1>
          <div className={styles.subtitle}>
            <Text
              text={TEXT.context}
              params={{ ...params, partnerName: props.partnerName || props.partnerId }}
            />
          </div>
        </div>
      )}
      content={PlanningSmbGuidedWizardContent}
      contentProps={{ props, editor, params }}
      onClose={editor.close}
    />
  )
}
